import type { ReactNode } from 'react';

import type { DialogContentProps } from '@radix-ui/react-dialog';
import {
  Close,
  Description,
  Content as DialogContent,
  Overlay,
  Portal,
  Root,
  Title,
  Trigger,
} from '@radix-ui/react-dialog';
import { X } from '@remarkable/ark-icons';
import { Spinner } from '@remarkable/ark-web';
import clsx from 'clsx';

import { cn } from 'src/utils/classNamesHelper';
import { withMaxWidth } from 'src/utils/withMaxWidth';

import { Button } from './Button';

export interface ModalProps {
  title?: ReactNode;
  loading?: boolean;
}

export const exitAnimationDuration = 200; // ms

const Content = ({
  title,
  className,
  children,
  loading,
  ...props
}: DialogContentProps & ModalProps) => {
  return (
    <Portal>
      <Overlay
        className={clsx(
          'fixed inset-0 z-[60] bg-gray-900/60 transition-all',
          'motion-safe:data-[state=closed]:animate-fade-out motion-safe:data-[state=open]:animate-fade-in',
          'motion-reduce:data-[state=closed]:opacity-0 motion-reduce:data-[state=open]:opacity-1'
        )}
        onClick={(e) => e.stopPropagation()}
      />
      <DialogContent
        className={cn(
          'rounded-sm fixed top-1/2 left-1/2 z-[61] max-h-[calc(100%-32px)] w-[calc(100%-32px)] overflow-y-auto bg-neutral-light-2 px-16 pt-48 pb-32 text-center shadow-xl focus:outline-hidden ls:py-48 lm:px-32',
          'motion-safe:data-[state=closed]:animate-modal-out motion-safe:data-[state=open]:animate-modal-in',
          'motion-reduce:-translate-x-1/2 motion-reduce:-translate-y-1/2',
          withMaxWidth('max-w-lm', className)
        )}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            {title && (
              <Title className="mb-32 responsive-heading-md">{title}</Title>
            )}

            {children}
          </>
        )}

        <Close asChild>
          <Button
            className="absolute top-[10px] right-[10px]"
            variant="tertiary-neutral"
            size="medium"
            data-cy="close-modal"
            aria-label="Close"
            onClick={(e) => e.stopPropagation()}
          >
            <X />
          </Button>
        </Close>
      </DialogContent>
    </Portal>
  );
};

export { Close, Content, Description, Root, Title, Trigger };
